import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { faPrint } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
} from '@repo/videa-components/src/index';
import { useAtom, useSetAtom } from 'jotai';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { ZuubPatientData } from '../../@types/zuub/contracts';
import { PatientNamePrivacy } from '../../common-components/composed/PatientNamePrivacy';
import { useGenerateBenefitsPdf } from '../../react-query/rcm/useGenerateBenefitsPdf';
import { useHasBenefitsWritebackSupport } from '../../react-query/rcm/useHasBenefitsWritebackSupport';
import { usePushBenefitsToPMS } from '../../react-query/rcm/usePushBenefitsToPMS';
import { useGetZuubInsuranceDetail } from '../../react-query/useGetZuubInsuranceDetail';
import { usePreferences } from '../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import { toBusinessName } from '../../utils/string';
import { PrivacyButton } from '../patientList/PrivacyButton';
import { useShouldHideHuddle } from '../patientList/useShouldHideHuddle';

import {
  benefitsNetworkSelectionsByPayerAtom,
  selectedNetworkIndexAtom,
  selectedPlanRawAtom,
} from './atoms';
import { BenefitsPdfContext } from './benefitsPage/context/BenefitsPdfContext';
import {
  findOutOfNetworkIndex,
  getNetworksForPlan,
  isPlanActive,
} from './helpers';

export const BenefitsPageTopbar = () => {
  // params
  const { patientId } = useParams<{ patientId: string }>();

  // hooks
  const history = useHistory();
  const { t } = useLingui();
  const hideHuddle = useShouldHideHuddle();
  const { trackEvent } = useTrackEvent();
  const { data: prefs } = usePreferences();
  const defaultOutOfNetwork = prefs?.defaultOutOfNetwork ?? false;

  // atoms
  const setSelectedPlanRaw = useSetAtom(selectedPlanRawAtom);
  const [selectedNetworkIndex, setSelectedNetworkIndex] = useAtom(
    selectedNetworkIndexAtom,
  );
  const [networkSelectionsByPayer, setNetworkSelectionsByPayer] = useAtom(
    benefitsNetworkSelectionsByPayerAtom,
  );

  // state
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);

  // hooks
  const {
    isLoading: isPdfLoading,
    mutate: generatePdf,
    reset: resetPdfMutation,
  } = useGenerateBenefitsPdf();

  const {
    isLoading: isPushingToPMS,
    mutate: pushToPMS,
    reset: resetPushToPMSMutation,
  } = usePushBenefitsToPMS();

  // Fetch detailed insurance data for this patient via API
  const { data: insuranceDetailData } = useGetZuubInsuranceDetail({
    pmsPatientId: patientId,
    enabled: !!patientId,
  });

  const currentPatientData: ZuubPatientData | null = useMemo(() => {
    if (!insuranceDetailData?.found) return null;
    return {
      pmsPatientId: insuranceDetailData.pmsPatientId,
      primaryProviderId: insuranceDetailData.primaryProviderId,
      patientName: insuranceDetailData.patientName,
      appointmentDate: insuranceDetailData.appointmentDate,
      lastRun: insuranceDetailData.lastRun,
      hasMultiplePlans: insuranceDetailData.hasMultiplePlans,
      totalPlans: insuranceDetailData.totalPlans,
      detailedData: insuranceDetailData.detailedData,
    };
  }, [insuranceDetailData]);

  const hasBenefitsWritebackSupport =
    useHasBenefitsWritebackSupport().data ?? false;
  const shouldShowPushToPMS = hasBenefitsWritebackSupport;
  const pdfContext = useContext(BenefitsPdfContext);

  // Get the currently selected plan (v4) by index
  const currentPlanRaw =
    currentPatientData?.detailedData?.[selectedPlanIndex] ?? null;

  // Networks for the second dropdown (network name)
  const availableNetworks = useMemo(
    () => getNetworksForPlan(currentPlanRaw),
    [currentPlanRaw],
  );

  // Derive patient name from Zuub data
  const patientName = currentPatientData?.patientName || 'Unknown Patient';

  // Plan options for first dropdown: "PayerName" + grey "PlanType (Primary)" only for primary
  const planOptions = useMemo(() => {
    const data = currentPatientData?.detailedData ?? [];
    return data.map((plan, index) => {
      const payerLabel = toBusinessName(plan.payerName ?? '');
      const planTypePart = plan.plan?.planType ? ` ${plan.plan.planType}` : '';
      const orderPart = plan.order === 0 ? ` (${t`Primary`})` : '';
      const suffix = `${planTypePart}${orderPart}`.trim();
      return { index, payerLabel, suffix, plan };
    });
  }, [currentPatientData, t]);

  const handleBackToPatientList = () => {
    trackEvent(mixpanelEvents.autoVerifyBenefitsBackToList, {
      pmsPatientId: patientId,
    });
    history.push('/auto-verify');
  };

  const handleGeneratePdf = useCallback(() => {
    if (!currentPlanRaw) return;

    trackEvent(mixpanelEvents.autoVerifyBenefitsDownloadPdf, {
      pmsPatientId: patientId,
      payerName: currentPlanRaw.payerName,
      unifiedAppointmentId: currentPlanRaw.unifiedAppointmentId,
    });

    generatePdf(
      {
        getBlob: pdfContext?.generatePdfFromDom,
        plan: currentPlanRaw,
        patientName,
        unifiedAppointmentId: currentPlanRaw.unifiedAppointmentId,
      },
      {
        onSuccess: () => {
          toast(
            <Toast
              variant="success"
              title={t`PDF generated successfully!`}
              description={t`Your benefits summary PDF has been downloaded.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          resetPdfMutation();
        },
        onError: (error) => {
          console.error('Failed to generate PDF:', error);
          toast(
            <Toast
              variant="error"
              title={t`PDF generation failed`}
              description={t`Failed to generate PDF. Please try again or contact support if the problem persists.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          resetPdfMutation();
        },
      },
    );
  }, [
    currentPlanRaw,
    patientName,
    patientId,
    generatePdf,
    pdfContext,
    resetPdfMutation,
    trackEvent,
    t,
  ]);

  // Handler for pushing to PMS
  const handlePushToPMS = useCallback(() => {
    if (!currentPlanRaw) return;

    trackEvent(mixpanelEvents.autoVerifyBenefitsPushToPms, {
      pmsPatientId: currentPlanRaw.pmsPatientId,
      payerName: currentPlanRaw.payerName,
      unifiedAppointmentId: currentPlanRaw.unifiedAppointmentId,
    });

    pushToPMS(
      {
        getBlob: pdfContext?.generatePdfFromDom,
        plan: currentPlanRaw,
        unifiedAppointmentId: currentPlanRaw.unifiedAppointmentId,
        pmsPatientId: currentPlanRaw.pmsPatientId,
      },
      {
        onSuccess: () => {
          toast(
            <Toast
              variant="success"
              title={t`Success`}
              description={t`Successfully pushed to PMS`}
            />,
            { closeButton: true, position: 'top-right' },
          );
          resetPushToPMSMutation();
        },
        onError: (err) => {
          console.error('Error pushing to PMS:', err);
          toast(
            <Toast
              variant="error"
              title={t`Error`}
              description={t`Failed to push to PMS. Please try again.`}
            />,
            { closeButton: true, position: 'top-right' },
          );
          resetPushToPMSMutation();
        },
      },
    );
  }, [
    currentPlanRaw,
    pdfContext,
    pushToPMS,
    resetPushToPMSMutation,
    trackEvent,
    t,
  ]);

  // Sync selected plan raw whenever we have data (including after refetch). Use a stable identity so refetches don't reset network.
  useEffect(() => {
    setSelectedPlanRaw(currentPlanRaw);
  }, [currentPlanRaw, setSelectedPlanRaw]);

  // Clamp plan index when data changes
  useEffect(() => {
    const planCount = currentPatientData?.detailedData?.length ?? 0;
    if (planCount > 0 && selectedPlanIndex >= planCount) {
      setSelectedPlanIndex(0);
    }
  }, [currentPatientData?.detailedData?.length, selectedPlanIndex]);

  // Restore per-payer+provider network selection from localStorage; fallback to OON (if pref) or first network.
  const primaryProviderId = currentPatientData?.primaryProviderId;
  useEffect(() => {
    if (availableNetworks.length > 0) {
      const payerId = currentPlanRaw?.payerId;
      if (payerId != null) {
        const persistKey = `${payerId}:${primaryProviderId ?? ''}`;
        const savedNetworkName = networkSelectionsByPayer[persistKey];

        if (savedNetworkName) {
          const exactIdx = availableNetworks.findIndex(
            (network) => network.name === savedNetworkName,
          );
          if (exactIdx >= 0) {
            setSelectedNetworkIndex(exactIdx);
            return;
          }
        }
      }

      // When defaultOutOfNetwork preference is enabled, try to select the OON network.
      if (defaultOutOfNetwork) {
        const oonIdx = findOutOfNetworkIndex(availableNetworks);
        if (oonIdx >= 0) {
          setSelectedNetworkIndex(oonIdx);
          return;
        }
      }

      setSelectedNetworkIndex(0);
    }
  }, [
    availableNetworks,
    currentPlanRaw?.payerId,
    primaryProviderId,
    defaultOutOfNetwork,
    networkSelectionsByPayer,
    setSelectedNetworkIndex,
  ]);

  return (
    <div className="vai-bg-white vai-px-4 vai-py-6 vai-shadow-sm vai-z-10 vai-h-[124px]">
      {/* First Row - Back to Patient List and Action Buttons */}
      <div className="vai-flex vai-items-center vai-justify-between vai-h-8 vai-mb-4">
        <button
          onClick={handleBackToPatientList}
          className="vai-flex vai-items-center vai-gap-2 vai-text-gray-700 hover:vai-text-gray-900 vai-transition-colors vai-duration-200"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="vai-text-sm" />
          <span className="vai-text-sm vai-font-medium">
            {hideHuddle ? (
              <Trans>All Patients</Trans>
            ) : (
              <Trans>Dashboard</Trans>
            )}
          </span>
        </button>
      </div>

      {/* Second Row - Patient Name, Selects, and Actions */}
      <div className="vai-flex vai-items-center vai-justify-between vai-h-10">
        {/* Left side - Patient Name and Selects */}
        <div className="vai-flex vai-items-center vai-gap-4">
          {/* Patient Name - aligned with Back to Patient List */}
          <h1 className="vai-text-xl vai-font-bold vai-text-gray-900 vai-whitespace-nowrap vai-ml-4">
            <PatientNamePrivacy name={patientName} noWrap={true} />
          </h1>

          {/* Vertical Divider */}
          <div className="vai-h-10 vai-w-px vai-bg-gray-300 vai-mx-2"></div>

          {/* Insurance Provider Select: "PayerName PlanType (Primary)" */}
          <Select
            value={
              planOptions.length > 0 && selectedPlanIndex < planOptions.length
                ? String(selectedPlanIndex)
                : undefined
            }
            onValueChange={(value) => {
              const idx = Number(value);
              const selectedPlan = planOptions[idx];
              trackEvent(mixpanelEvents.autoVerifyBenefitsPlanSwitch, {
                pmsPatientId: patientId,
                planIndex: idx,
                payerName: selectedPlan?.payerLabel,
              });
              setSelectedPlanIndex(idx);
            }}
          >
            <SelectTrigger className="vai-w-auto vai-whitespace-nowrap [&>span]:vai-mr-2">
              <SelectValue placeholder={t`Select insurance provider`} />
            </SelectTrigger>
            <SelectContent>
              {planOptions.map(({ index, payerLabel, suffix, plan }) => {
                const planStatus = plan?.plan?.status ?? null;
                const isActive = isPlanActive(planStatus);

                return (
                  <SelectItem
                    key={index}
                    value={String(index)}
                    disabled={!isActive}
                  >
                    <div className="vai-flex vai-items-center vai-gap-1">
                      {payerLabel}
                      {suffix && (
                        <span className="vai-text-gray-500 vai-text-sm">
                          {' '}
                          {suffix}
                        </span>
                      )}
                      {!isActive && (
                        <span className="vai-text-gray-500 vai-text-sm">
                          {' '}
                          (<Trans>Inactive</Trans>)
                        </span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Network Select - per-network data from v4 */}
          {availableNetworks.length > 0 && (
            <Select
              value={
                availableNetworks[selectedNetworkIndex]
                  ? String(selectedNetworkIndex)
                  : '0'
              }
              onValueChange={(value) => {
                const idx = Number(value);
                trackEvent(mixpanelEvents.autoVerifyBenefitsNetworkSwitch, {
                  pmsPatientId: patientId,
                  networkIndex: idx,
                  networkName: availableNetworks[idx]?.name,
                });
                const payerId = currentPlanRaw?.payerId;
                const selectedNetworkName = availableNetworks[idx]?.name;
                if (
                  payerId != null &&
                  typeof selectedNetworkName === 'string' &&
                  selectedNetworkName.length > 0
                ) {
                  const persistKey = `${payerId}:${primaryProviderId ?? ''}`;
                  setNetworkSelectionsByPayer((prev) => ({
                    ...prev,
                    [persistKey]: selectedNetworkName,
                  }));
                }
                setSelectedNetworkIndex(idx);
              }}
            >
              <SelectTrigger className="vai-w-auto">
                <SelectValue placeholder={t`Network`} />
              </SelectTrigger>
              <SelectContent>
                {availableNetworks.map((network, index) => (
                  <SelectItem key={network.name} value={String(index)}>
                    {network.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Right side - Push to PMS, Privacy Button and Print Button */}
        <div className="vai-flex vai-items-center vai-gap-2">
          {/* Push to PMS Button */}
          {shouldShowPushToPMS && (
            <Button
              disabled={!currentPlanRaw}
              loading={isPushingToPMS}
              onClick={handlePushToPMS}
            >
              <Trans>Push to PMS</Trans>
            </Button>
          )}
          <PrivacyButton source="AUTOVERIFY" />
          {/* Print Button */}
          <Button
            disabled={!currentPlanRaw}
            loading={isPdfLoading}
            title={t`Print`}
            variant="outline"
            onClick={handleGeneratePdf}
          >
            <FontAwesomeIcon className="vai-text-zinc-900" icon={faPrint} />
          </Button>
        </div>

        {/* Right side - Search Input */}
        {/* <div className="vai-relative vai-w-80">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-text-sm vai-pointer-events-none vai-z-10"
          />
          <Input
            placeholder="Search ADA code or service"
            className="vai-pl-8"
          />
        </div> */}
      </div>
    </div>
  );
};
